import { useOutletContext } from 'react-router-dom';
import { useMemo } from 'react';
import { MONTHS } from '@/shared/utils/dateHelpers';
import { fmtAUD, fmtCurrency } from '@/shared/utils/formatCurrency';
import { buildBudgetSuggestions } from '@/shared/utils/buildBudgetSuggestions';
import { S } from '@/shared/constants/styles';
import BudgetOverview from '../components/BudgetOverview';
import BudgetCopyPanel from '../components/BudgetCopyPanel';
import BudgetSuggestionsBanner from '../components/BudgetSuggestionsBanner';
import { useIsMobile } from '@/shared/hooks/useMediaQuery';
import { useBudgets } from '../hooks/useBudgets';
import { useHouseholdData } from '@/app/providers/HouseholdDataProvider';
import { useCategoriesContext } from '@/app/providers/CategoriesProvider';
import { useSettingsContext } from '@/app/providers/SettingsProvider';
import { usePeriod } from '@/app/providers/PeriodProvider';
import { DEFAULT_DATA } from '@/shared/constants/config';

export default function BudgetsPage() {
  const { dashboard } = useOutletContext();
  const { data, householdId } = useHouseholdData();
  const { expenseMap, incomeList } = useCategoriesContext();
  const { t, isDark, currency, budgetMode } = useSettingsContext();
  const { selMonth, selYear } = usePeriod();
  const mobile = useIsMobile();

  const safe = data || DEFAULT_DATA;
  const budgets = safe.budgets || {};
  const { monthTxns, monthIncome, monthExpenses } = dashboard;

  const budgetsHook = useBudgets({ householdId, data: safe, budgets, expenseMap, selMonth, selYear });
  const {
    budgetKey, currentBudgets, totalBudgeted,
    availableBudgetMonths, categoryMonths,
    copyBudgetOpen, setCopyBudgetOpen,
    copySource, setCopySource,
    copyMode, setCopyMode,
    copyCategory, setCopyCategory,
    deleteConfirm, setDeleteConfirm,
    saveBudget, copyBudgetFrom,
  } = budgetsHook;

  const allTxns = safe.txns || [];
  const budgetSuggestions = useMemo(
    () => buildBudgetSuggestions(allTxns, currentBudgets, expenseMap, incomeList, selMonth, selYear),
    [allTxns, currentBudgets, expenseMap, incomeList, selMonth, selYear]
  );

  const StatCard = ({ label, value, color = "#0f172a" }) => (
    <div style={{ background: t.cardBg, borderRadius: 14, padding: 18, boxShadow: "0 1px 4px rgba(0,0,0,0.04)", border: "1px solid " + t.cardBorder }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: t.textMuted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 800, color, lineHeight: 1 }}>{typeof value === "number" ? fmtCurrency(value, currency) : value}</div>
    </div>
  );

  return (
    <div>
      {budgetMode === "envelope" && (
        <div style={{ background: t.cardBg, borderRadius: 14, padding: 20, border: "1px solid " + t.cardBorder, marginBottom: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: t.text }}>Income Allocation</div>
            <div style={{ fontSize: 12, color: t.textMuted }}>{MONTHS[selMonth] + " " + selYear}</div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr" : "1fr 1fr 1fr", gap: 12, marginBottom: 14 }}>
            <div><div style={{ fontSize: 10, color: t.textMuted, textTransform: "uppercase", fontWeight: 700, letterSpacing: 0.5 }}>Income</div><div style={{ fontSize: 20, fontWeight: 800, color: "#16a34a" }}>{currency + fmtAUD(monthIncome)}</div></div>
            <div><div style={{ fontSize: 10, color: t.textMuted, textTransform: "uppercase", fontWeight: 700, letterSpacing: 0.5 }}>Allocated</div><div style={{ fontSize: 20, fontWeight: 800, color: "#2563eb" }}>{currency + fmtAUD(totalBudgeted)}</div></div>
            <div><div style={{ fontSize: 10, color: t.textMuted, textTransform: "uppercase", fontWeight: 700, letterSpacing: 0.5 }}>Unallocated</div><div style={{ fontSize: 20, fontWeight: 800, color: monthIncome - totalBudgeted > 0 ? "#f59e0b" : monthIncome - totalBudgeted === 0 ? "#16a34a" : "#ef4444" }}>{fmtCurrency(monthIncome - totalBudgeted, currency)}</div></div>
          </div>
          <div style={{ width: "100%", background: isDark ? "#334155" : "#e2e8f0", borderRadius: 20, height: 10, overflow: "hidden", position: "relative" }}>
            <div style={{ position: "absolute", left: 0, top: 0, width: monthIncome > 0 ? Math.min(100, totalBudgeted / monthIncome * 100) + "%" : "0%", height: 10, background: "#3b82f6", borderRadius: 20, transition: "width 0.3s" }} />
            <div style={{ position: "absolute", left: 0, top: 0, width: monthIncome > 0 ? Math.min(100, monthExpenses / monthIncome * 100) + "%" : "0%", height: 10, background: "#ef4444", borderRadius: 20, transition: "width 0.3s" }} />
          </div>
          <div style={{ display: "flex", gap: 16, marginTop: 8, fontSize: 10, color: t.textMuted }}>
            <span>{"\u25A0"} <span style={{ color: "#3b82f6" }}>Allocated</span></span>
            <span>{"\u25A0"} <span style={{ color: "#ef4444" }}>Spent</span></span>
            <span>{"\u25A0"} <span style={{ color: isDark ? "#334155" : "#e2e8f0" }}>Unallocated</span></span>
          </div>
          {monthIncome > 0 && totalBudgeted < monthIncome && (
            <div style={{ marginTop: 10, fontSize: 12, color: t.warningText, background: t.warningBg, padding: "8px 12px", borderRadius: 8 }}>
              {"\u{1F4A1} You have " + currency + fmtAUD(monthIncome - totalBudgeted) + " unallocated. Assign every dollar to reach your savings goals faster!"}
            </div>
          )}
          {monthIncome > 0 && totalBudgeted === monthIncome && (
            <div style={{ marginTop: 10, fontSize: 12, color: t.successText, background: t.successBg, padding: "8px 12px", borderRadius: 8 }}>
              {"\u{1F389} Every dollar is assigned! You're fully allocated for " + MONTHS[selMonth] + "."}
            </div>
          )}
          {totalBudgeted > monthIncome && monthIncome > 0 && (
            <div style={{ marginTop: 10, fontSize: 12, color: t.errorText, background: t.errorBg, padding: "8px 12px", borderRadius: 8 }}>
              {"\u{26A0}\u{FE0F} You've allocated " + currency + fmtAUD(totalBudgeted - monthIncome) + " more than your income. Adjust your envelopes."}
            </div>
          )}
        </div>
      )}

      {budgetMode === "standard" && (
        <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr" : "1fr 1fr 1fr", gap: 14, marginBottom: 20 }}>
          <StatCard label="Budgeted" value={totalBudgeted} color="#2563eb" />
          <StatCard label="Spent" value={monthExpenses} color="#ef4444" />
          <StatCard label="Remaining" value={totalBudgeted - monthExpenses} color={totalBudgeted - monthExpenses >= 0 ? "#16a34a" : "#ef4444"} />
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
        <span style={{ fontSize: 13, color: t.textMuted }}>
          {"Monthly budget for " + MONTHS[selMonth] + " " + selYear}{Object.keys(currentBudgets).length === 0 ? " (not set yet)" : ""}
        </span>
        {availableBudgetMonths.length > 0 && (
          <button onClick={() => { setCopyBudgetOpen(!copyBudgetOpen); setCopySource(""); setCopyCategory(""); setCopyMode("entire"); setDeleteConfirm(null); }} style={{ ...S.btnSm(copyBudgetOpen ? "#e2e8f0" : "#2563eb"), color: copyBudgetOpen ? "#475569" : "white" }}>
            {copyBudgetOpen ? "Close" : "\u{1F4CB} Copy budget from..."}
          </button>
        )}
      </div>

      {copyBudgetOpen && (
        <BudgetCopyPanel
          selMonth={selMonth} selYear={selYear}
          expenseMap={expenseMap} budgets={budgets}
          currentBudgets={currentBudgets}
          availableBudgetMonths={availableBudgetMonths}
          categoryMonths={categoryMonths}
          copySource={copySource} setCopySource={setCopySource}
          copyMode={copyMode} setCopyMode={setCopyMode}
          copyCategory={copyCategory} setCopyCategory={setCopyCategory}
          deleteConfirm={deleteConfirm} setDeleteConfirm={setDeleteConfirm}
          copyBudgetFrom={copyBudgetFrom}
          currency={currency}
        />
      )}

      <BudgetSuggestionsBanner
        suggestions={budgetSuggestions}
        saveBudget={saveBudget}
        currency={currency}
        theme={t}
      />
      <BudgetOverview
        expenseMap={expenseMap}
        currentBudgets={currentBudgets}
        monthTxns={monthTxns}
        saveBudget={saveBudget}
        budgetMode={budgetMode}
        currency={currency}
        theme={t}
        isDark={isDark}
      />
    </div>
  );
}
